import {
  appendOpenCloudUIText,
  boundOpenCloudUIMessageParts,
  type OpenCloudUIMessage,
  type OpenCloudUIMessagePart,
  type OpenCloudUIMessagePatch,
} from "@opencloud/agent-protocol";

export const MAX_LEGACY_LIVE_DELTA_MESSAGES = 64;

export interface LegacyLiveDelta {
  messageId: string;
  delta: string;
}

/**
 * Applies a safe, already-validated SSE projection patch without making the
 * browser stream authoritative. The next durable snapshot may replace this
 * optimistic projection at any time.
 */
export function applyOpenCloudUIMessagePatch(
  messages: readonly OpenCloudUIMessage[],
  patch: OpenCloudUIMessagePatch,
  options: { runCreatedAt?: string } = {},
): OpenCloudUIMessage[] {
  const index = messages.findIndex(({ id }) => id === patch.messageId);
  const current = index >= 0 ? messages[index] : undefined;
  const message: OpenCloudUIMessage = current ?? {
    id: patch.messageId,
    role: patch.role,
    metadata: {
      schemaVersion: 1,
      threadId: patch.metadata.threadId,
      runId: patch.metadata.runId,
      source: "run",
      createdAt:
        patch.operation === "append-text"
          ? patch.metadata.occurredAt
          : (options.runCreatedAt ??
            patch.metadata.messageCreatedAt ??
            patch.metadata.occurredAt),
      sequence: null,
    },
    parts: [],
  };

  const parts = boundOpenCloudUIMessageParts(
    patch.operation === "append-text" && patch.part.type === "text"
      ? appendTextPart(message.parts, patch.part)
      : upsertPart(message.parts, patch.part),
  );
  if (!current && parts.length === 0) return [...messages];
  const nextMessage = {
    ...message,
    parts,
  } satisfies OpenCloudUIMessage;
  const next = [...messages];
  if (index >= 0) next[index] = nextMessage;
  else next.push(nextMessage);
  return next.sort(compareMessages);
}

/**
 * Rebases incomplete text over a newer durable snapshot. A previous-only
 * message survives unrelated cursor advances, while a same-ID durable message
 * owns its reconstructed prefix. Canonical transcript completion or a settled
 * run removes a previous-only streaming overlay.
 */
export function reconcileOpenCloudUIMessageSnapshot(
  durable: readonly OpenCloudUIMessage[],
  previous: readonly OpenCloudUIMessage[],
  completedMessageIds: ReadonlySet<string>,
  terminalRunIds: ReadonlySet<string> = new Set(),
): OpenCloudUIMessage[] {
  const next = [...durable];
  const durableById = new Map(
    next.map((message, index) => [message.id, index]),
  );
  for (const message of previous) {
    if (
      completedMessageIds.has(message.id) ||
      (message.metadata.runId !== null &&
        terminalRunIds.has(message.metadata.runId)) ||
      !message.parts.some(
        (part) => part.type === "text" && part.state === "streaming",
      )
    ) {
      continue;
    }
    const index = durableById.get(message.id);
    if (index === undefined) {
      durableById.set(message.id, next.length);
      next.push(message);
      continue;
    }
    // The caller has already rejected stale snapshot cursors. When the newer
    // durable projection knows this message, it owns the reconstructed prefix
    // and must not regress to an older optimistic copy.
  }
  return next.sort(compareMessages);
}

/** Stable identity for every AI SDK-shaped part that supplies one. */
export function openCloudUIMessagePartId(
  messageId: string,
  part: OpenCloudUIMessagePart,
): string {
  if (part.type === "text") return messageId;
  if (part.type === "reasoning") return part.id;
  if (part.type === "dynamic-tool") return part.toolCallId;
  return part.id;
}

/**
 * Keeps the legacy-only streaming fallback bounded while old servers and
 * cached clients coexist with the typed UI projection.
 */
export function appendLegacyLiveDelta(
  deltas: ReadonlyMap<string, string>,
  messageId: string,
  delta: string,
): Map<string, string> {
  const next = new Map(deltas);
  const content = appendOpenCloudUIText(next.get(messageId) ?? "", delta);
  // Refresh insertion order so eviction retains the most recently active IDs.
  next.delete(messageId);
  next.set(messageId, content);
  while (next.size > MAX_LEGACY_LIVE_DELTA_MESSAGES) {
    const oldest = next.keys().next().value;
    if (oldest === undefined) break;
    next.delete(oldest);
  }
  return next;
}

/** Returns a legacy delta only when no validated typed patch replaced it. */
export function legacyLiveDeltaFromDisplay(
  value: Record<string, unknown> | null,
  typedPatchAccepted: boolean,
): LegacyLiveDelta | null {
  if (
    typedPatchAccepted ||
    value?.type !== "message.delta" ||
    typeof value.messageId !== "string" ||
    typeof value.delta !== "string"
  ) {
    return null;
  }
  return { messageId: value.messageId, delta: value.delta };
}

function appendTextPart(
  parts: readonly OpenCloudUIMessagePart[],
  delta: Extract<OpenCloudUIMessagePart, { type: "text" }>,
): OpenCloudUIMessagePart[] {
  const next = [...parts];
  for (let index = next.length - 1; index >= 0; index -= 1) {
    const current = next[index];
    if (current?.type !== "text" || current.state === "done") continue;
    next[index] = {
      ...current,
      text: appendOpenCloudUIText(current.text, delta.text),
      state: "streaming",
    };
    return next;
  }
  next.push({
    ...delta,
    text: appendOpenCloudUIText("", delta.text),
    state: "streaming",
  });
  return next;
}

function upsertPart(
  parts: readonly OpenCloudUIMessagePart[],
  value: OpenCloudUIMessagePart,
): OpenCloudUIMessagePart[] {
  const identity = partIdentity(value);
  const index = parts.findIndex((part) => partIdentity(part) === identity);
  if (index < 0) {
    // A completion whose start has already left the bounded live window must
    // not resurrect that old identity as the newest part. The scheduled
    // durable snapshot will include it only if its original coordinates still
    // belong in the canonical window.
    if (
      (value.type === "dynamic-tool" &&
        value.toolMetadata.startedAt === null) ||
      (value.type === "data-child" && value.data.outcome !== null)
    ) {
      return [...parts];
    }
    return [...parts, value];
  }
  const next = [...parts];
  const current = next[index];
  if (
    current?.type === "dynamic-tool" &&
    value.type === "dynamic-tool" &&
    value.toolMetadata.startedAt === null &&
    current.toolMetadata.startedAt !== null
  ) {
    next[index] = {
      ...value,
      title: current.title,
      input: current.input,
      toolMetadata: {
        ...value.toolMetadata,
        kind: current.toolMetadata.kind,
        startedAt: current.toolMetadata.startedAt,
        sequence: current.toolMetadata.sequence,
      },
    };
    return next;
  }
  if (current?.type === "data-plan" && value.type === "data-plan") {
    next[index] = {
      ...value,
      data: {
        ...value.data,
        occurredAt: current.data.occurredAt,
      },
    };
    return next;
  }
  if (
    current?.type === "data-child" &&
    value.type === "data-child"
  ) {
    next[index] = {
      ...value,
      data: {
        ...value.data,
        // A recovered start may supply a better name after completion.
        ...(current.data.outcome !== null && value.data.outcome === null ? {
          state: current.data.state,
          outcome: current.data.outcome,
          updatedAt: current.data.updatedAt,
        } : {}),
        purpose: value.data.purpose ?? current.data.purpose,
        occurredAt: current.data.occurredAt,
      },
    };
    return next;
  }
  next[index] = value;
  return next;
}

function partIdentity(part: OpenCloudUIMessagePart): string {
  if (part.type === "text") return "text";
  if (part.type === "reasoning") return `reasoning:${part.id}`;
  if (part.type === "dynamic-tool") return `tool:${part.toolCallId}`;
  return `${part.type}:${part.id}`;
}

function compareMessages(
  left: OpenCloudUIMessage,
  right: OpenCloudUIMessage,
): number {
  return (
    left.metadata.createdAt.localeCompare(right.metadata.createdAt) ||
    (left.metadata.sequence ?? Number.MAX_SAFE_INTEGER) -
      (right.metadata.sequence ?? Number.MAX_SAFE_INTEGER) ||
    left.id.localeCompare(right.id)
  );
}
